import React from 'react';
import { Link } from 'react-router-dom';
import { X } from 'lucide-react';
import { useCart } from '../contexts/CartContext';

function formatPrice(cents) {
  return '$' + (cents / 100).toFixed(2);
}

export default function CartDrawer({ open, onClose }) {
  const { cart, updateItem, removeItem } = useCart();

  const freeShippingRemaining = cart.free_shipping_threshold_cents
    ? Math.max(0, cart.free_shipping_threshold_cents - cart.subtotal_cents)
    : 0;
  const freeShippingProgress = cart.free_shipping_threshold_cents
    ? Math.min(100, (cart.subtotal_cents / cart.free_shipping_threshold_cents) * 100)
    : 0;

  return (
    <>
      {open && (
        <div
          onClick={onClose}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
            zIndex: 300, transition: 'opacity 0.2s',
          }}
        />
      )}

      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0,
        width: '400px', maxWidth: '90vw',
        background: 'var(--bg-2)', zIndex: 301,
        transform: open ? 'translateX(0)' : 'translateX(100%)',
        transition: 'transform 0.3s ease',
        display: 'flex', flexDirection: 'column',
        boxShadow: '-4px 0 32px rgba(0,0,0,0.4)',
        borderLeft: '1px solid rgba(201,168,76,0.08)',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', borderBottom: '1px solid rgba(201,168,76,0.08)' }}>
          <h3 style={{ margin: 0, fontWeight: 300 }}>Your Cart ({cart.item_count})</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', padding: '4px', color: 'var(--cream-dim)' }} aria-label="Close cart">
            <X size={20} />
          </button>
        </div>

        {/* Free shipping progress */}
        {cart.items.length > 0 && (
          <div style={{ padding: '12px 24px', background: 'var(--surface)' }}>
            {freeShippingRemaining > 0 ? (
              <>
                <p style={{ fontSize: '0.75rem', color: 'var(--cream-dim)', marginBottom: '6px' }}>
                  Add <strong style={{ color: 'var(--gold)' }}>{formatPrice(freeShippingRemaining)}</strong> more for free shipping
                </p>
                <div style={{ height: '3px', background: 'rgba(201,168,76,0.1)', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${freeShippingProgress}%`, background: 'var(--gold)', transition: 'width 0.3s' }} />
                </div>
              </>
            ) : (
              <p style={{ fontSize: '0.75rem', color: 'var(--gold)', fontWeight: '500' }}>
                You've unlocked free shipping!
              </p>
            )}
          </div>
        )}

        {/* Items */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '0 24px' }}>
          {cart.items.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--cream-dim)' }}>
              <p style={{ marginBottom: '16px' }}>Your cart is empty</p>
              <Link to="/shop" className="btn btn-primary btn-sm" onClick={onClose}>Shop Now</Link>
            </div>
          ) : (
            cart.items.map(item => (
              <div className="cart-item" key={item.item_id}>
                <div className="cart-item-img">
                  {item.image_url ? <img src={item.image_url} alt="" /> : null}
                </div>
                <div className="cart-item-details">
                  <div className="cart-item-name">{item.product_name}</div>
                  {(item.variant || item.color) && (
                    <div className="cart-item-variant" style={{ textTransform: item.variant ? 'none' : 'capitalize' }}>
                      {item.variant || item.color}
                    </div>
                  )}
                  <div className="cart-item-actions">
                    <button className="qty-btn" onClick={() => updateItem(item.item_id, { quantity: item.quantity - 1 })}>-</button>
                    <span>{item.quantity}</span>
                    <button className="qty-btn" onClick={() => updateItem(item.item_id, { quantity: item.quantity + 1 })}>+</button>
                    <button onClick={() => removeItem(item.item_id)} style={{ background: 'none', border: 'none', color: 'var(--error)', fontSize: '0.6rem', marginLeft: 'auto', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '0.15em' }}>Remove</button>
                  </div>
                </div>
                <div className="cart-item-price">{formatPrice(item.price_cents * item.quantity)}</div>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        {cart.items.length > 0 && (
          <div style={{ padding: '20px 24px', borderTop: '1px solid rgba(201,168,76,0.08)' }}>
            <div className="cart-total-row"><span>Subtotal</span><span>{formatPrice(cart.subtotal_cents)}</span></div>
            <div className="cart-total-row">
              <span>Shipping</span>
              <span>{cart.shipping_cents === 0 ? 'FREE' : formatPrice(cart.shipping_cents)}</span>
            </div>
            <div className="cart-total-row grand"><span>Total</span><span>{formatPrice(cart.total_cents)}</span></div>
            <Link to="/checkout" className="btn btn-gold btn-lg" onClick={onClose} style={{ width: '100%', marginTop: '16px', display: 'flex' }}>
              Checkout
            </Link>
            <button onClick={onClose} style={{ width: '100%', textAlign: 'center', marginTop: '8px', background: 'none', border: 'none', color: 'var(--cream-dim)', fontSize: '0.8rem', padding: '8px' }}>
              Continue Shopping
            </button>
          </div>
        )}
      </div>
    </>
  );
}
